import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useVisualViewportRect} from '@amblique/king-living/app/hooks/use-visual-viewport-rect';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {resolveComplexFlyoutHeroPreviewFromJourney, resolveComplexGallery} from '@amblique/king-living/app/utils/gallery-state-utils';
import {buildLayersFromSelections} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {productUrlBuilder} from '@amblique/king-living/app/utils/url';

/** Complex customize flyout gallery — shared by desktop LHS and mobile drawer. */
export function useComplexGalleryState({
  panelMode,
  masterProduct,
  isCoverPickerOpen = false,
  committedSelections,
  draftSelections,
  galleryPreviewProduct,
}) {
  return useMemo(() => {
    if (panelMode !== 'customize') return null;

    const state = resolveComplexGallery({
      committedSelections,
      draftSelections,
      galleryPreviewProduct,
      isCoverPickerOpen,
      masterProduct,
    });

    return {
      ...state,
      galleryLayers: state.galleryLayers.length > 0 ?
        state.galleryLayers :
        buildLayersFromSelections(draftSelections),
    };
  }, [
    panelMode,
    masterProduct,
    isCoverPickerOpen,
    committedSelections,
    draftSelections,
    galleryPreviewProduct
  ]);
}

function pickCoverVariationAttributes(draftSelections, fallbackAttributes) {
  const draft = draftSelections?.cover?.variationValues;

  if (draft && typeof draft === 'object' && Object.keys(draft).length > 0) {
    return draft;
  }

  return fallbackAttributes && typeof fallbackAttributes === 'object' ? fallbackAttributes : {};
}

/**
 * Encapsulates all state, effects, and handlers for ConfigurationSelector.
 * Keeps the render component lean and focused on JSX.
 *
 * ## Panel vs flyout state machine (customize mode)
 *
 * - **Open flyout:** `openFlyout()` in context sets `flyoutDraftState.isOpen === true`. If the parent
 *   panel thinks it is closed (`isOpen === false`), the effect below calls `onOpen?.()` so the drawer
 *   opens in sync.
 * - **Close with discard (X / overlay):** `handleClose` calls `cancelFlyout()` then `onClose?.(true)`.
 *   Parent (ProductView) receives `discardFlyoutDraft=true` — scroll logic in usePdpCustomizeMirrors
 *   skips the post-commit scroll. Gallery preview is automatically cleared via context cancelFlyout.
 * - **Done (commit):** `commitFlyout()` clears draft (`isOpen === false`) while selections are already
 *   in `committedSelection`. Effect sees `!isFlyoutOpen && isOpen && panelMode === 'customize'` and
 *   calls `onClose?.(false)` so the shell closes **without** discard.
 * - **Choose-layout mode:** `handleClose` calls `onClose?.(false)` only when selection matches the
 *   current PDP master; if the user picked another master, `navigate` to that PDP.
 * - **Choose-layout Done:** optional `navigate` when selection differs, then `onClose?.(false)`.
 *
 * ## View Details state
 *
 * `viewDetailsProduct` (Object | null) drives the full-screen gallery portal.
 *   - null = portal closed
 *   - Object = product to display in portal
 *
 * Gallery preview (`flyoutDraftState.galleryPreview`) lives in CustomizeJourneyContext, updated via
 * `updateGalleryPreview` from `handleCustomizeGalleryProductChange`. Cleared by `cancelFlyout`/`commitFlyout`.
 */
export function useConfigurationSelector({
  configProducts,
  isOpen,
  onClose,
  onOpen,
  panelMode,
  product,
  selectedId,
}) {
  const navigate = useNavigation();
  const isMobile = useBreakpointValue({base: true, lg: false});
  const {
    cancelFlyout,
    committedSelection,
    flyoutDraftState,
    updateGalleryPreview,
  } = useCustomizeJourney();

  const [panelHeaderInfo, setPanelHeaderInfo] = useState(null);
  const drawerEscHandlerRef = useRef(null);

  // Full-screen gallery portal product. null = portal closed.
  const [viewDetailsProduct, setViewDetailsProduct] = useState(null);

  /** Multi-type slot-cards: dynamic "{short} details" gallery header + long description in portal. */
  const [viewDetailsProductHeaderUi, setViewDetailsProductHeaderUi] = useState(false);

  const customizeGalleryVariationValuesForPanel = useMemo(
    () => pickCoverVariationAttributes(
      flyoutDraftState?.draftSelections,
      flyoutDraftState?.galleryPreview?.variationValues ?? {}
    ),
    [flyoutDraftState?.draftSelections, flyoutDraftState?.galleryPreview?.variationValues]
  );

  const flyoutHeroPreviewContext = useMemo(
    () => ({
      committedSelections: committedSelection?.selections,
      draftSelections: flyoutDraftState?.draftSelections,
      masterProduct: product,
    }),
    [committedSelection?.selections, flyoutDraftState?.draftSelections, product]
  );

  const drawerViewportRect = useVisualViewportRect(Boolean(isOpen && isMobile));
  const isFlyoutOpen = flyoutDraftState.isOpen;

  const currentProductId = product?.id ?? product?.productId;
  const effectiveSelectedId = selectedId ?? currentProductId;
  const hasMultipleConfigs = configProducts.length > 1;
  const selectedProduct =
    configProducts.find((p) => (p.productId || p.id) === effectiveSelectedId) ??
    configProducts[0] ??
    null;

  /**
   * User dismissed the panel (X, overlay, escape).
   * Sub-views and gallery overlays register via setDrawerEscHandler — Esc / overlay
   * dismisses the inner layer first (back), not the whole customize flyout.
   */
  const setDrawerEscHandler = useCallback((handler) => {
    drawerEscHandlerRef.current = handler;
  }, []);

  const handleClose = useCallback(() => {
    if (drawerEscHandlerRef.current?.() === true) {
      return;
    }

    if (panelMode === 'customize') {
      cancelFlyout();
      onClose?.(true);

      return;
    }

    const layoutTargetId = selectedProduct?.productId ?? selectedProduct?.id;

    if (
      panelMode === 'choose-layout' &&
      layoutTargetId &&
      layoutTargetId !== currentProductId
    ) {
      const pdpUrl = productUrlBuilder(selectedProduct) || null;

      if (pdpUrl) {
        navigate(pdpUrl);
      }
      onClose?.(false);

      return;
    }

    onClose?.(false);
  }, [
    cancelFlyout,
    currentProductId,
    navigate,
    onClose,
    panelMode,
    selectedProduct
  ]);

  // Sync flyout open state with drawer/panel open state (see state machine in file JSDoc).
  // Pass committedSelection.mode so the scroll hook can target the correct anchor.
  const committedModeRef = useRef(committedSelection.mode);

  committedModeRef.current = committedSelection.mode;

  useEffect(() => {
    if (isFlyoutOpen && !isOpen) {
      onOpen?.();
    } else if (!isFlyoutOpen && isOpen && panelMode === 'customize') {
      onClose?.(false, committedModeRef.current);
    }
  }, [isFlyoutOpen, isOpen, onOpen, onClose, panelMode]);

  // Clear panel header when flyout closes
  useEffect(() => {
    if (!isFlyoutOpen) setPanelHeaderInfo(null);
  }, [isFlyoutOpen]);

  const handleDoneClick = useCallback(() => {
    const layoutTargetId = selectedProduct?.productId ?? selectedProduct?.id;

    if (
      panelMode === 'choose-layout' &&
      layoutTargetId &&
      layoutTargetId !== currentProductId
    ) {
      const pdpUrl = productUrlBuilder(selectedProduct) || null;

      if (pdpUrl) {
        navigate(pdpUrl);
      }
    }
    onClose?.(false);
  }, [currentProductId, navigate, onClose, panelMode, selectedProduct]);

  /**
   * Unified "View Details" handler — opens the full-screen gallery portal.
   *
   * customize mode: called with a product object (may carry _selectedVariationValues).
   *   - Strips _selectedVariationValues, calls updateGalleryPreview (drives desktop left column).
   *   - Sets viewDetailsProduct (drives mobile portal).
   *
   * choose-layout mode: called with a productId string (SelectorTile) OR product object (expand button).
   *   - Resolves product from configProducts when string.
   *   - Does NOT call updateGalleryPreview — that would corrupt the desktop gallery state.
   */
  const openViewDetails = useCallback((productOrId, options = {}) => {
    const productDetailHeaderUi = options?.productDetailHeaderUi === true;

    if (panelMode === 'customize') {
      const coverVariationValues = productOrId?._selectedVariationValues;
      const cleanProduct = coverVariationValues ?
        (({_selectedVariationValues: _, ...rest}) => rest)(productOrId) :
        productOrId;

      updateGalleryPreview({
        product: resolveComplexFlyoutHeroPreviewFromJourney({
          ...flyoutHeroPreviewContext,
          galleryPreviewProduct: cleanProduct,
        }),
        variationValues: coverVariationValues ?? {},
        viewDetailsProduct: cleanProduct,
      });

      setViewDetailsProduct(cleanProduct);
      setViewDetailsProductHeaderUi(productDetailHeaderUi);
    } else {
      setViewDetailsProductHeaderUi(false);
      const resolvedProduct = typeof productOrId === 'string' ?
        configProducts.find((p) => (p.productId || p.id) === productOrId) ?? null :
        productOrId ?? null;

      setViewDetailsProduct(resolvedProduct);
    }
  }, [
    configProducts,
    flyoutHeroPreviewContext,
    panelMode,
    updateGalleryPreview
  ]);

  const closeViewDetails = useCallback(() => {
    setViewDetailsProduct(null);
    setViewDetailsProductHeaderUi(false);
  }, []);

  // Complex flyout: onGalleryProductChange → updateGalleryPreview (hero + viewDetailsProduct).
  const handleCustomizeGalleryProductChange = useCallback(({
    product: galleryPreviewProduct,
    retainFlyoutLhsSnapshot = false,
    selectedVariationAttributes,
    viewDetailsProduct: resolvedViewDetailsProduct,
  } = {}) => {
    // Skip while SCAPI/picker transition — avoids LHS master flash and premature clear.
    if (retainFlyoutLhsSnapshot) {
      return;
    }

    const nextVariation = (selectedVariationAttributes && typeof selectedVariationAttributes === 'object') ?
      selectedVariationAttributes :
      {};

    updateGalleryPreview({
      product: resolveComplexFlyoutHeroPreviewFromJourney({
        ...flyoutHeroPreviewContext,
        galleryPreviewProduct: galleryPreviewProduct ?? null,
      }),
      variationValues: nextVariation,
      viewDetailsProduct: resolvedViewDetailsProduct ?? null,
    });
  }, [flyoutHeroPreviewContext, updateGalleryPreview]);

  const handleCustomizeSlotPreview = useCallback((previewProduct) => {
    updateGalleryPreview({
      product: resolveComplexFlyoutHeroPreviewFromJourney({
        ...flyoutHeroPreviewContext,
        galleryPreviewProduct: previewProduct,
      }),
      variationValues: flyoutDraftState?.galleryPreview?.variationValues ?? {},
    });
  }, [
    flyoutDraftState?.galleryPreview?.variationValues,
    flyoutHeroPreviewContext,
    updateGalleryPreview
  ]);

  return {
    closeViewDetails,
    currentProductId,
    customizeGalleryVariationValuesForPanel,
    drawerViewportRect,
    effectiveSelectedId,
    hasMultipleConfigs,
    isFlyoutOpen,
    isMobile,
    openViewDetails,
    panelHeaderInfo,
    selectedProduct,
    viewDetailsProduct,
    viewDetailsProductHeaderUi,
    handleClose,
    handleCustomizeGalleryProductChange,
    handleCustomizeSlotPreview,
    handleDoneClick,
    setPanelHeaderInfo,
    setDrawerEscHandler,
  };
}
